import { useQuery } from '@tanstack/react-query'
import { useDebouncedValue } from './useDebouncedValue'
import * as api from '../api/calculatorApi'
import type {
  BlobStorageRequest, LogicAppsRequest, FunctionsRequest,
  AppServiceRequest, BandwidthRequest,
  LogAnalyticsIngestionRequest, LogAnalyticsRetentionRequest,
  LogAnalyticsSearchJobRequest, LogAnalyticsRestoreRequest,
  NetworkingRequest,
  FirewallRequest,
  FrontDoorRequest,
  DataFactoryRequest,
  VirtualDesktopRequest,
  SQLDatabaseRequest,
  OpenAIRequest,
  ManagedDisksRequest,
  KeyVaultRequest,
  AzureDevOpsRequest,
  SiteRecoveryRequest,
} from '../api/types'

// Each hook:
//   1. Accepts the current form inputs
//   2. Debounces them by 400ms before triggering a fetch
//   3. Returns { data, isLoading, isError, error }
//   4. Only fetches if inputs are valid (not null/undefined for required fields)

export function useBlobStorageCalc(inputs: BlobStorageRequest | null) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['blob-storage', debounced],
    queryFn: () => api.calculateBlobStorage(debounced!),
    enabled: debounced !== null && debounced.storageTB >= 0,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useLogicAppsCalc(inputs: LogicAppsRequest | null) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['logic-apps', debounced],
    queryFn: () => api.calculateLogicApps(debounced!),
    enabled: debounced !== null,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useFunctionsCalc(inputs: FunctionsRequest | null) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['functions', debounced],
    queryFn: () => api.calculateFunctions(debounced!),
    enabled: debounced !== null && debounced.executionsPerMonth >= 0,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useAppServiceCalc(inputs: AppServiceRequest | null) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['app-service', debounced],
    queryFn: () => api.calculateAppService(debounced!),
    enabled: debounced !== null,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useBandwidthCalc(inputs: BandwidthRequest | null) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['bandwidth', debounced],
    queryFn: () => api.calculateBandwidth(debounced!),
    enabled: debounced !== null,
    staleTime: 30_000,
    retry: 1,
  })
}

export function useLogAnalyticsIngestionCalc(inputs: LogAnalyticsIngestionRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['la-ingestion', debounced],
    queryFn: () => api.calculateLogAnalyticsIngestion(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useLogAnalyticsRetentionCalc(inputs: LogAnalyticsRetentionRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['la-retention', debounced],
    queryFn: () => api.calculateLogAnalyticsRetention(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useLogAnalyticsSearchJobCalc(inputs: LogAnalyticsSearchJobRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['la-search-job', debounced],
    queryFn: () => api.calculateLogAnalyticsSearchJob(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useLogAnalyticsRestoreCalc(inputs: LogAnalyticsRestoreRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['la-restore', debounced],
    queryFn: () => api.calculateLogAnalyticsRestore(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useNetworkingCalc(inputs: NetworkingRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['networking', debounced],
    queryFn: () => api.calculateNetworking(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useFirewallCalc(inputs: FirewallRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['firewall', debounced],
    queryFn: () => api.calculateFirewall(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useFrontDoorCalc(inputs: FrontDoorRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['front-door', debounced],
    queryFn: () => api.calculateFrontDoor(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useDataFactoryCalc(inputs: DataFactoryRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['data-factory', debounced],
    queryFn: () => api.calculateDataFactory(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useVirtualDesktopCalc(inputs: VirtualDesktopRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['virtual-desktop', debounced],
    queryFn: () => api.calculateVirtualDesktop(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useSQLDatabaseCalc(inputs: SQLDatabaseRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['sql-database', debounced],
    queryFn: () => api.calculateSQLDatabase(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useOpenAICalc(inputs: OpenAIRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['openai', debounced],
    queryFn: () => api.calculateOpenAI(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useManagedDisksCalc(inputs: ManagedDisksRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['managed-disks', debounced],
    queryFn: () => api.calculateManagedDisks(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useKeyVaultCalc(inputs: KeyVaultRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['key-vault', debounced],
    queryFn: () => api.calculateKeyVault(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useAzureDevOpsCalc(inputs: AzureDevOpsRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['azure-devops', debounced],
    queryFn: () => api.calculateAzureDevOps(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useSiteRecoveryCalc(inputs: SiteRecoveryRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['site-recovery', debounced],
    queryFn: () => api.calculateSiteRecovery(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useAksCalc(inputs: import('../api/types').AksRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['aks', debounced],
    queryFn: () => api.calculateAks(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useSqlMiCalc(inputs: import('../api/types').SqlMiRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['sql-mi', debounced],
    queryFn: () => api.calculateSqlMi(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useAppGatewayCalc(inputs: import('../api/types').AppGatewayRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['application-gateway', debounced],
    queryFn: () => api.calculateAppGateway(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useVpnGatewayCalc(inputs: import('../api/types').VpnGatewayRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['vpn-gateway', debounced],
    queryFn: () => api.calculateVpnGateway(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useAzureBackupCalc(inputs: import('../api/types').AzureBackupRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['azure-backup', debounced],
    queryFn: () => api.calculateAzureBackup(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function useBastionCalc(inputs: import('../api/types').BastionRequest) {
  const debounced = useDebouncedValue(inputs, 400)
  return useQuery({
    queryKey: ['bastion', debounced],
    queryFn: () => api.calculateBastion(debounced),
    staleTime: 30_000,
    retry: 1,
  })
}

export function usePricesLastUpdated() {
  return useQuery({
    queryKey: ['prices-last-updated'],
    queryFn: () => api.getPricesLastUpdated(),
    staleTime: 600_000, // 10 minutes
  })
}
